// src/pages/CustomerJobs.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";

// Import Services
import { getCustomerById, updateCustomer } from "../services/customerService";
import { seedTopLevelJob } from "../services/jobService";

// Import Components
import CustomerHeader from "../components/customers/CustomerHeader";
import JobsHeader from "../components/customers/JobsHeader";
import JobsTable from "../components/customers/JobsTable";
import JobModal from "../components/customers/JobModal";
import EmptyJobsState from "../components/customers/EmptyJobsState";
import QuickAddJobModal from "../components/customers/QuickAddJobModal";

/**
 * CustomerJobs Component
 * Manages jobs for a specific customer with full CRUD functionality
 * Features quick add modal and comprehensive job management
 */
const CustomerJobs = () => {
  // Get customer ID and job ID from URL parameters
  const { customerId, jobId } = useParams();
  const navigate = useNavigate();
  const highlightJobId = jobId; // Use jobId from URL params instead of query params

  // Handle redirect if no ID is provided
  useEffect(() => {
    if (!customerId) {
      navigate("/customers");
    }
  }, [customerId, navigate]);

  // State for customer data
  const [customer, setCustomer] = useState({});

  // State for jobs management
  const [jobsList, setJobsList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [search, setSearch] = useState("");

  // State for modal visibility
  const [addJobModal, setAddJobModal] = useState(false);
  const [quickAddModal, setQuickAddModal] = useState(false);

  // Default job template with all required fields including address fields
  const [defaultParams] = useState({
    id: null,
    date: new Date().toLocaleDateString("en-GB"),
    hasUnderlay: false,
    hasGrippers: false,
    floorIsGood: false,
    oldFlooringRemoved: false,
    furnitureRemoval: false,
    concrete: false,
    doorsNeedCutting: 0,
    numberOfDoorPlateNeeded: 0,
    comments: "",
    plan: "",
    products: "",
    address: "",
    postcode: "",
  });

  // Current job parameters (for add/edit operations)
  const [params, setParams] = useState(
    JSON.parse(JSON.stringify(defaultParams)),
  );

  // Loading state for async operations
  const [loading, setLoading] = useState(true);

  /**
   * Fetch customer data and their jobs on component mount
   */
  useEffect(() => {
    if (customerId) {
      const fetchCustomerData = async () => {
        setLoading(true);

        try {
          const customerData = await getCustomerById(customerId);

          if (customerData) {
            setCustomer(customerData);

            if (customerData.jobs && Array.isArray(customerData.jobs)) {
              setJobsList(customerData.jobs);
            } else {
              setJobsList([]);
            }
          }
        } catch (error) {
          console.error("Error fetching customer data:", error);
          showMessage(
            `Failed to load customer data: ${error.message}`,
            "error",
          );
        } finally {
          setLoading(false);
        }
      };

      fetchCustomerData();
    }
  }, [customerId]);

  /**
   * Filter jobs based on search term
   */
  useEffect(() => {
    if (search.length !== 0) {
      setFilteredItems(
        jobsList.filter((item) => {
          return (
            item.date.toLowerCase().includes(search.toLowerCase()) ||
            item.address?.toLowerCase().includes(search.toLowerCase()) ||
            item.postcode?.toLowerCase().includes(search.toLowerCase())
          );
        }),
      );
    } else {
      setFilteredItems(jobsList);
    }
  }, [search, jobsList]);

  /**
   * Open quick add modal
   */
  const handleQuickAdd = () => {
    setQuickAddModal(true);
  };

  /**
   * Handle quick add save with address fields
   */
  const handleQuickAddSave = async (data) => {
    const today = new Date().toLocaleDateString("en-GB");
    const newJob = {
      id: uuidv4(),
      date: today,
      hasUnderlay: false,
      hasGrippers: false,
      floorIsGood: false,
      oldFlooringRemoved: false,
      furnitureRemoval: false,
      concrete: false,
      doorsNeedCutting: 0,
      numberOfDoorPlateNeeded: 0,
      comments: "",
      plan: data.plan,
      products: data.products,
      address: data.address,
      postcode: data.postcode,
      feeter: "",
    };

    try {
      // Update customer with new job
      await updateCustomer(customerId, {
        ...customer,
        jobs: [...jobsList, newJob],
      });

      // Also create the top-level jobs/{id} doc (id-aligned) so the fitter
      // portal, rooms mirror, and driver assignment can target it. Best-effort:
      // a failure must not block job creation — the rooms save self-heals it.
      // `customer` is passed so the job is born carrying the customer's name
      // and phone, which is what the fitter's job card shows.
      try {
        await seedTopLevelJob(customerId, newJob, customer);
      } catch (seedErr) {
        console.warn(
          "Top-level job seed failed (self-heals on first room save):",
          seedErr,
        );
      }

      // Update local state
      setJobsList([...jobsList, newJob]);
      showMessage("Job added successfully", "success");
      setQuickAddModal(false);
    } catch (error) {
      console.error("Error adding job:", error);
      showMessage("Failed to add job", "error");
    }
  };

  /**
   * Handle form field changes for full job modal
   */
  const handleInputChange = (e) => {
    if (!e.target) {
      if (typeof e === "string") {
        if (e === "") return;
        setParams({ ...params, date: e });
      } else {
        setParams({ ...params, date: e.value });
      }
      return;
    }

    const { value, id, type, checked, name } = e.target;
    const fieldName = name || id; // Support both name and id attributes

    if (type === "number") {
      if (value === "") {
        setParams({ ...params, [fieldName]: 0 });
      } else {
        setParams({ ...params, [fieldName]: Number(value) });
      }
    } else if (type === "checkbox") {
      setParams({ ...params, [fieldName]: checked });
    } else {
      setParams({ ...params, [fieldName]: value });
    }
  };

  /**
   * Save job (create or update) with address validation
   */
  const saveJob = async () => {
    // Validate required fields
    if (!params.date) {
      showMessage("Please enter a Date", "error");
      return;
    }

    if (!params.address || !params.postcode) {
      showMessage("Please enter Address and Postcode", "error");
      return;
    }

    try {
      if (params.id) {
        // Update existing job
        const updatedJobs = jobsList.map((job) =>
          job.id === params.id ? params : job,
        );

        await updateCustomer(customerId, {
          ...customer,
          jobs: updatedJobs,
        });

        setJobsList(updatedJobs);
        showMessage("Job updated successfully", "success");
      } else {
        // Create new job
        const newJob = {
          ...params,
          id: uuidv4(),
          feeter: "",
        };

        await updateCustomer(customerId, {
          ...customer,
          jobs: [...jobsList, newJob],
        });

        // Also create the top-level jobs/{id} doc (id-aligned) so the fitter
        // portal, rooms mirror, and driver assignment can target it. Best-effort:
        // a failure must not block job creation — the rooms save self-heals it.
        // `customer` is passed so the job is born carrying the customer's name
        // and phone, which is what the fitter's job card shows.
        try {
          await seedTopLevelJob(customerId, newJob, customer);
        } catch (seedErr) {
          console.warn(
            "Top-level job seed failed (self-heals on first room save):",
            seedErr,
          );
        }

        setJobsList([...jobsList, newJob]);
        showMessage("Job added successfully", "success");
      }

      setAddJobModal(false);
      setParams(JSON.parse(JSON.stringify(defaultParams)));
    } catch (error) {
      console.error("Error saving job:", error);
      showMessage("Failed to save job", "error");
    }
  };

  /**
   * Edit job - populate form with job data including address
   */
  const editJob = (job = null) => {
    const defaultValues = JSON.parse(JSON.stringify(defaultParams));

    if (job) {
      // Populate with existing job data
      setParams({
        ...defaultValues,
        ...job,
        // Ensure address fields are included
        address: job.address || customer.location || "",
        postcode: job.postcode || customer.postCode || "",
      });
    } else {
      // New job - use customer's address as default
      setParams({
        ...defaultValues,
        address: customer.location || "",
        postcode: customer.postCode || "",
      });
    }

    setAddJobModal(true);
  };

  /**
   * Delete job
   */
  const deleteJob = async (job = null) => {
    if (!job) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const updatedJobs = jobsList.filter((item) => item.id !== job.id);

        await updateCustomer(customerId, {
          ...customer,
          jobs: updatedJobs,
        });

        setJobsList(updatedJobs);
        showMessage("Job deleted successfully", "success");
      } catch (error) {
        console.error("Error deleting job:", error);
        showMessage("Failed to delete job", "error");
      }
    }
  };

  /**
   * Show toast message
   */
  const showMessage = (msg = "", type = "success") => {
    Swal.mixin({
      toast: true,
      position: "top",
      showConfirmButton: false,
      timer: 3000,
      customClass: { container: "toast" },
    }).fire({
      icon: type,
      title: msg,
      padding: "10px 20px",
    });
  };

  // Show loading spinner while data is being fetched
  if (loading || !customerId) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 bg-gray-50 min-h-screen">
      {/* Customer Information Header */}
      <CustomerHeader customer={customer} />

      {/* Jobs Management Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        {/* Jobs Header with Search and Actions */}
        <JobsHeader
          search={search}
          setSearch={setSearch}
          onAddJob={handleQuickAdd}
          onOpenModal={() => editJob()}
        />

        {/* Jobs Content */}
        {filteredItems.length === 0 ? (
          <EmptyJobsState onAddJob={() => editJob()} />
        ) : (
          <JobsTable
            jobs={filteredItems}
            onEditJob={editJob}
            onDeleteJob={deleteJob}
            customerId={customerId}
            highlightJobId={highlightJobId}
          />
        )}
      </div>

      {/* Job Add/Edit Modal */}
      <JobModal
        isOpen={addJobModal}
        onClose={() => setAddJobModal(false)}
        params={params}
        onInputChange={handleInputChange}
        onSave={saveJob}
      />

      {/* Quick Add Job Modal with customer address */}
      <QuickAddJobModal
        isOpen={quickAddModal}
        onClose={() => setQuickAddModal(false)}
        onSave={handleQuickAddSave}
        customerAddress={{
          address: customer.location || "",
          postcode: customer.postCode || "",
        }}
      />
    </div>
  );
};

export default CustomerJobs;
